if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      static ACCORDION_PREFS_KEY = 'product_accordion_preferences';
      static ACCORDION_PREFS_MAX_AGE = 30 * 24 * 60 * 60 * 1000; // 30 days
      static _accordionListenersInitialized = false;

      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.initializeAccordionStatePreservation();

        // FoxTheme.pubsub comes from theme.js, which can fail to execute on rare
        // sessions (stale cached bundle / vendor.js load failure). Guard so the
        // element upgrades without throwing instead of breaking the PDP. SHOPIFY-1YY.
        if (window.FoxTheme && FoxTheme.pubsub) {
          this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleOptionValueChange.bind(this)
          );
        }

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }

        // Apply user's saved accordion preferences
        this.applyAccordionPreferences();
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        if (this.mediaTimer) {
          clearInterval(this.mediaTimer);
          this.mediaTimer = null;
        }
      }

      initializeProductSwapUtility() {
        const self = this;

        // Capture accordion state BEFORE DOM replacement
        this.preProcessHtmlCallbacks.push((_oldNode) => {
          self._savedAccordionState = self.captureAccordionState();
        });

        // Restore accordion state and initialize utilities AFTER DOM replacement
        this.postProcessHtmlCallbacks.push((_newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();

          // Restore accordion state after DOM replacement
          if (self._savedAccordionState && self._savedAccordionState.length > 0) {
            requestAnimationFrame(() => {
              self.restoreAccordionState(self._savedAccordionState);
            });
          }
        });
      }

      /**
       * Initialize accordion state preservation for variant changes
       * Uses document-level event delegation to persist across section replacements
       */
      initializeAccordionStatePreservation() {
        // Only add document-level listeners once (shared across all product-info instances)
        if (this.constructor._accordionListenersInitialized) return;
        this.constructor._accordionListenersInitialized = true;

        // Store state in a module-level variable accessible to listeners
        let savedAccordionState = this.captureAccordionState();
        let captureTimeout = null;
        const self = this;

        // Listen for toggle events at document level (persists across DOM replacements)
        document.addEventListener('toggle', (event) => {
          if (event.target.tagName !== 'DETAILS') return;
          // Only handle details inside product-info
          const productInfo = event.target.closest('product-info');
          if (!productInfo) return;

          // Debounce state capture
          clearTimeout(captureTimeout);
          captureTimeout = setTimeout(() => {
            savedAccordionState = self.captureAccordionState();
            // Also save to localStorage for cross-PDP persistence
            self.saveAccordionPreferences(savedAccordionState);
          }, 50);
        }, true);

        // Listen for variant change events - restore state after section re-renders
        document.addEventListener('variant:changed', () => {
          // Delay to allow Shopify section rendering to complete
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (savedAccordionState && savedAccordionState.length > 0) {
                self.restoreAccordionState(savedAccordionState);
              }
            }, 100);
          });
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, allOptionsSelected, selectedOptionCount, totalOptionGroups } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        // Store selection state for use in callbacks
        this.allOptionsSelected = allOptionsSelected;
        this.selectedOptionCount = selectedOptionCount;
        this.totalOptionGroups = totalOptionGroups;

        // If not all options are selected, show placeholder state immediately
        if (!allOptionsSelected && totalOptionGroups > 1) {
          this.setIncompleteSelection();
          return;
        }

        // Skip fetch on initial load if we already have the correct variant rendered
        // This prevents unnecessary flicker when the page already shows the right data
        if (event.isInitialLoad && this.currentVariant) {
          return;
        }

        // Show loading state while fetching new variant data
        this.setLoadingState(true);

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? 'product-info[id^=\'MainProduct\']' : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            // A combined-listing swap can cross the cf-test eligibility gate,
            // where the fetched page uses the OTHER pdp-restructure stylesheet
            // (they style the same selectors and must never mix with the other
            // arm's markup) — sync the head link when it differs.
            const currentPdpCss = document.querySelector('link[href*="pdp-restructure"]');
            const fetchedPdpCss = html.querySelector('link[href*="pdp-restructure"]');
            if (currentPdpCss && fetchedPdpCss && currentPdpCss.getAttribute('href') !== fetchedPdpCss.getAttribute('href')) {
              currentPdpCss.setAttribute('href', fetchedPdpCss.getAttribute('href'));
            }
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant);

          if (!variant) {
            this.setLoadingState(false);
            this.setUnavailable();
            return;
          }

          // Clear loading state before updating with new data
          this.setLoadingState(false);

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (_source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('MainSku');
          updateSourceFromDestination('MobileSku');
          updateSourceFromDestination('MainBrand');
          updateSourceFromDestination('MobileVendor');
          updateSourceFromDestination('Vendor');
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('VariantDisclaimer', ({ innerHTML }) => innerHTML.trim() === '');
          updateSourceFromDestination('ShortDescription', ({ innerHTML }) => innerHTML.trim() === '');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          const sizeChartOld = document.querySelector(`#SizeChart-${this.sectionId}`);
          const sizeChartNew = html.querySelector(`#SizeChart-${this.sectionId}`);
          HTMLUpdateUtility.viewTransition(
            sizeChartOld,
            sizeChartNew,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          // Update product specifications/metafields for the new variant
          const specsOld = document.querySelector(`#ProductSpecs-${this.sectionId}`);
          const specsNew = html.querySelector(`#ProductSpecs-${this.sectionId}`);
          HTMLUpdateUtility.viewTransition(
            specsOld,
            specsNew,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          // Update promotion alert for the new variant
          const promotionAlertOld = document.querySelector(`#PromotionAlert-${this.sectionId}`);
          const promotionAlertNew = html.querySelector(`#PromotionAlert-${this.sectionId}`);
          if (promotionAlertOld && promotionAlertNew) {
            HTMLUpdateUtility.viewTransition(
              promotionAlertOld,
              promotionAlertNew,
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else if (promotionAlertOld && !promotionAlertNew) {
            // New variant has no promotion - clear the old one
            promotionAlertOld.innerHTML = '';
            promotionAlertOld.classList.add('hidden');
          } else if (!promotionAlertOld && promotionAlertNew) {
            // Edge case: old didn't exist but new does - shouldn't happen with placeholder
            console.warn('PromotionAlert: new content exists but no placeholder in DOM');
          }

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          if (window.FoxTheme?.pubsub) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });
          }
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        // Include customer type for Cloudflare cache keying (B2B vs B2C)
        const customerType = window.FoxTheme?.customerType || 'b2c';
        params.push(`ct=${customerType}`);

        // Alternate-template pages (view= URL param, e.g. Shoplift template tests)
        // must re-send view= here: Section Rendering resolves the template from the
        // request URL, so without it the section comes back from the default template.
        const templateView = new URLSearchParams(window.location.search).get('view');
        templateView && params.push(`view=${encodeURIComponent(templateView)}`);

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('Product info fetch error:', error);
              // Clear loading state on error
              this.setLoadingState(false);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        // Mutate only the variant param — rebuilding the query string from
        // scratch erased view=, which every section fetch on this page reads
        // from location.search (v2's URL update preserves params the same way).
        const params = new URLSearchParams(window.location.search);
        if (variantId) {
          params.set('variant', variantId);
        } else {
          params.delete('variant');
        }
        const qs = params.toString();
        window.history.replaceState({}, '', qs ? `${url}?${qs}` : url);
      }

      updateVariantInputs(variant) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variant?.id ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));

            // Keep partial-backorder inventory state in sync with the selected
            // variant (issue #1346). theme.js ProductForm.handleFormSubmit reads
            // these to decide whether to write _qty_backordered_at_add at ATC.
            if (variant) {
              productForm.dataset.inventoryQuantity = variant.inventory_quantity ?? 0;
              productForm.dataset.inventoryPolicy = variant.inventory_policy ?? '';
            } else {
              delete productForm.dataset.inventoryQuantity;
              delete productForm.dataset.inventoryPolicy;
            }
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            if (typeof productMedia.init === 'function') productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          let attempts = 0;
          const maxAttempts = 50; // 5 seconds max (50 * 100ms)
          this.mediaTimer = setInterval(() => {
            attempts++;
            if (setActiveMedia() || attempts >= maxAttempts) {
              clearInterval(this.mediaTimer);
              this.mediaTimer = null;
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareUrl = `${window.shopUrl}${this.dataset.url}?variant=${variantId}`;

        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (shareButton && shareButton.updateUrl) shareButton.updateUrl(shareUrl);

        // Keep the social share links (Facebook/X/Pinterest) pointed at the
        // selected variant too. They are static <a> tags rendered by Liquid at
        // page load, so without this a post-load variant change would share the
        // default variant while the copy field shows the selected one. Rewrite
        // the shared-URL query param (`u` for Facebook, `url` for X/Pinterest).
        this.querySelectorAll('.sharing__links a[href]').forEach((link) => {
          try {
            const linkUrl = new URL(link.href, window.location.origin);
            let changed = false;
            ['u', 'url'].forEach((param) => {
              if (linkUrl.searchParams.has(param)) {
                linkUrl.searchParams.set(param, shareUrl);
                changed = true;
              }
            });
            if (changed) link.href = linkUrl.toString();
          } catch {
            /* malformed href — leave as-is */
          }
        });
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          // Add null check for addButtonText to prevent errors (SHOPIFY-ZW, SHOPIFY-ZV)
          if (text && addButtonText) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          if (addButtonText) addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        // When no valid variant exists, show placeholder prompting user to select different options
        // This is friendlier than showing "Unavailable" and encourages exploration
        this.toggleAddButton(true, FoxTheme.variantStrings.selectOptions);

        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        // Show placeholder for price instead of hiding
        if (price) {
          price.classList.remove('hidden');
          price.innerHTML = `<span class="f-price__placeholder text-subtext">${FoxTheme.variantStrings.selectOptionsForPrice}</span>`;
        }

        // Show placeholder for inventory instead of hiding
        if (inventory) {
          inventory.classList.remove('hidden');
          const inventoryElement = inventory.querySelector('.product__inventory') || inventory;
          inventoryElement.innerHTML = `<span class="product__inventory-placeholder text-subtext">${FoxTheme.variantStrings.selectOptionsForAvailability}</span>`;
        }

        // Hide SKU when no valid variant
        if (sku) sku.classList.add('hidden');
      }

      // Loading spinner HTML for injection
      get loadingSpinnerHtml() {
        return `<div class="loading__spinner">
          <svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>`;
      }

      setLoadingState(isLoading) {
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);

        if (isLoading) {
          // Add loading classes and inject spinners
          if (price) {
            price.classList.add('product__price-loading');
            if (!price.querySelector('.loading__spinner')) {
              price.insertAdjacentHTML('beforeend', this.loadingSpinnerHtml);
            }
          }
          if (inventory) {
            inventory.classList.add('product__inventory-loading');
            if (!inventory.querySelector('.loading__spinner')) {
              inventory.insertAdjacentHTML('beforeend', this.loadingSpinnerHtml);
            }
          }
        } else {
          // Remove loading classes and spinners
          if (price) {
            price.classList.remove('product__price-loading');
            price.querySelector('.loading__spinner')?.remove();
          }
          if (inventory) {
            inventory.classList.remove('product__inventory-loading');
            inventory.querySelector('.loading__spinner')?.remove();
          }
        }
      }

      setIncompleteSelection() {
        // Show "Select options" button text
        this.toggleAddButton(true, FoxTheme.variantStrings.selectOptions);

        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        // Show placeholder for price instead of hiding
        if (price) {
          price.classList.remove('hidden');
          price.innerHTML = `<span class="f-price__placeholder text-subtext">${FoxTheme.variantStrings.selectOptionsForPrice}</span>`;
        }

        // Show placeholder for inventory instead of hiding
        if (inventory) {
          inventory.classList.remove('hidden');
          const inventoryElement = inventory.querySelector('.product__inventory') || inventory;
          inventoryElement.innerHTML = `<span class="product__inventory-placeholder text-subtext">${FoxTheme.variantStrings.selectOptionsForAvailability}</span>`;
        }

        // Hide SKU when selection incomplete
        if (sku) sku.classList.add('hidden');

        // Clear variant input
        this.updateVariantInputs(null);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section') && window.FoxTheme && FoxTheme.pubsub) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        if (!window.FoxTheme?.pubsub) return;
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        // Include customer type for Cloudflare cache keying
        const customerType = window.FoxTheme?.customerType || 'b2c';
        // Re-send view= so alternate-template pages get their own template's
        // section back (see buildRequestUrlWithParams)
        const templateView = new URLSearchParams(window.location.search).get('view');
        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}&ct=${customerType}${templateView ? `&view=${encodeURIComponent(templateView)}` : ''}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        if (window.FoxTheme?.pubsub) {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
            data: {
              sectionId,
              productId,
              parsedHTML,
            },
          });
        }

        this.setQuantityBoundries();
      }

      /**
       * Capture categories of all open accordion/details elements
       * Uses data-category attribute which is consistent across variants and PDPs
       * @returns {string[]} Array of category names that are open
       */
      captureAccordionState() {
        const openCategories = [];
        // Query current DOM - `this` may be stale after section replacement
        const productInfo = document.querySelector('product-info');
        if (!productInfo) return openCategories;

        productInfo.querySelectorAll('details[open]').forEach(details => {
          // Use data-category attribute (e.g., "dimensions", "appearance", "bulb-information")
          const category = details.dataset.category;
          if (category && !openCategories.includes(category)) {
            openCategories.push(category);
          }
          // Also support id-based accordions as fallback
          if (!category && details.id) {
            const normalizedId = this.normalizeAccordionId(details.id);
            if (normalizedId && !openCategories.includes(normalizedId)) {
              openCategories.push(normalizedId);
            }
          }
        });
        return openCategories;
      }

      /**
       * Normalize accordion ID by removing section-specific suffixes
       * This allows preferences to persist across different PDPs
       * @param {string} id - The full accordion ID
       * @returns {string} Normalized ID without section suffix
       */
      normalizeAccordionId(id) {
        if (!id) return '';
        // Pattern: "Details-{type}-{block_id}-{section_id}" or "Details-{block_id}-{section_id}"
        // We want to keep: "Details-{type}" or "Details-{block_id}" (first two parts)
        const parts = id.split('-');
        if (parts.length >= 2 && parts[0] === 'Details') {
          // Keep "Details-{next_part}" as the normalized ID
          return `${parts[0]}-${parts[1]}`;
        }
        return id;
      }

      /**
       * Restore open state for previously-open accordions
       * Queries the current DOM to find product-info (handles element replacement)
       * @param {string[]} categories - Array of category names or normalized IDs to restore
       */
      restoreAccordionState(categories) {
        if (!categories || categories.length === 0) return;

        // Query current DOM - `this` may be stale after section replacement
        const productInfo = document.querySelector('product-info');
        if (!productInfo) return;

        categories.forEach(category => {
          productInfo.querySelectorAll('details').forEach(details => {
            // Match by data-category attribute
            const detailCategory = details.dataset.category;
            const matchesByCategory = detailCategory && detailCategory === category;

            // Also support id-based matching as fallback
            const matchesById = details.id && this.normalizeAccordionId(details.id) === category;

            if (matchesByCategory || matchesById) {
              if (!details.open) {
                details.open = true;
                details.setAttribute('aria-expanded', 'true');
              }
            }
          });
        });
      }

      /**
       * Save accordion preferences to localStorage for cross-PDP persistence
       * @param {string[]} openIds - Array of normalized accordion IDs that are open
       */
      saveAccordionPreferences(openIds) {
        try {
          const prefs = {
            openAccordions: openIds,
            timestamp: Date.now()
          };
          localStorage.setItem(this.constructor.ACCORDION_PREFS_KEY, JSON.stringify(prefs));
        } catch (_e) {
          // localStorage may be unavailable (private browsing, quota exceeded)
        }
      }

      /**
       * Load accordion preferences from localStorage
       * @returns {string[]} Array of normalized accordion IDs that should be open
       */
      loadAccordionPreferences() {
        try {
          const stored = localStorage.getItem(this.constructor.ACCORDION_PREFS_KEY);
          if (!stored) return [];

          const prefs = JSON.parse(stored);

          // Check if preferences are expired
          if (Date.now() - prefs.timestamp > this.constructor.ACCORDION_PREFS_MAX_AGE) {
            localStorage.removeItem(this.constructor.ACCORDION_PREFS_KEY);
            return [];
          }

          return prefs.openAccordions || [];
        } catch (_e) {
          // Invalid JSON or localStorage unavailable
          return [];
        }
      }

      /**
       * Apply saved accordion preferences on page load
       * Called after DOM is ready to restore user's preferred accordion states
       */
      applyAccordionPreferences() {
        const savedPrefs = this.loadAccordionPreferences();
        if (savedPrefs.length > 0) {
          this.restoreAccordionState(savedPrefs);
        }
      }

    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        if (this.buttonEl) {
          this.buttonEl.addEventListener('click', this.onClick.bind(this), {
            signal: this.abortController.signal,
          });
        }
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}
